import { useTranslation } from "@vixi/localization";
import { CourseConversation, CourseData, CourseTabComponentProps } from "@vixi/types";
import _ from "lodash";
import { ComponentProps, useCallback, useEffect, useMemo } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { CourseStepDivider, CourseStepPaddingWrapper, EditableBox } from ".";
import { CourseTabTitle } from "../CourseTabs/CourseTabTitle";
import { cn } from "@/lib/utils";
import { Button } from "@/components/base/button";
import { TransparentTextArea } from "@/components/input";
import ViewList, { ViewListItem } from "@/components/layout/ViewList";
import { useViewList } from "@/context/ViewListContext";
import useUiStore from "@/store/ui.state";
import { ConversationPagination } from "../conversation-pagination";
import { useCourseEditorContext } from "../context";

export interface ConversationStepProps
  extends CourseTabComponentProps,
    CourseData.ConversationStep {}
export interface ConversationFormValues extends Pick<CourseConversation, "messages"> {}

const SpeakerHeading = (props: ComponentProps<"h3">) => (
  <h3
    {...props}
    className={cn(
      "fs-22 text-primary mx-auto w-fit min-w-20 p-2 text-center font-semibold",
      props.className,
    )}
  />
);

/**
 * ConversationStep - A component for displaying a conversation-type step in the course editor
 * @param props.path [index, "lessons", index, "conversations", index]
 */
export function ConversationStep({ path: propsPath, ...props }: ConversationStepProps) {
  const { t } = useTranslation();
  const viewList = useViewList({
    hasEndStep: false,
    hasStartStep: false,
  });
  const path = useMemo(() => [...propsPath, "messages"], [propsPath]);

  // Global state management
  const { setValue } = useCourseEditorContext();
  const { mode } = useUiStore((state) => state.courseEditor);
  const disabled = mode === "view";

  // Form handling
  const { register, watch, control, getValues } = useForm<ConversationFormValues>({
    defaultValues: {
      messages: props.messages,
    },
  });

  const { insert: insertSentence, remove: removeSentence } = useFieldArray({
    name: "messages",
    control,
  });

  const addSentence = () => {
    insertSentence(viewList.activeStep + 1, {
      authorMessage: "",
      learnerMessage: "",
      order: viewList.activeStep + 1,
    });
    viewList.nextStep();
  };

  // Update state from form
  const updateState = useCallback(() => {
    if (mode !== "edit") return;

    setValue(path, getValues("messages"), {
      condition: (_prev, parent) => {
        return (parent as CourseData.ConversationStep).id === props.id;
      },
    });
  }, [path]);
  const debouncedUpdate = useMemo(() => _.debounce(updateState, 500), [updateState]);

  useEffect(() => {
    return updateState;
  }, []);
  watch(async () => debouncedUpdate());

  const messages = watch("messages");

  return (
    <>
      <CourseTabTitle className="mx-auto max-w-fit">{t("Course.Conversation")}</CourseTabTitle>

      <ViewList {...viewList}>
        {messages?.map((_lines, index) => {
          return (
            <ViewListItem key={index}>
              <div className="flex min-h-0 flex-1 flex-col">
                <CourseStepPaddingWrapper>
                  <EditableBox direction="column">
                    <SpeakerHeading>{t("Conversation.Speaker.You")}</SpeakerHeading>
                    <TransparentTextArea
                      minRows={4}
                      maxRows={4}
                      disabled={disabled}
                      className="fs-20"
                      {...register(`messages.${index}.authorMessage`)}
                    />
                  </EditableBox>
                </CourseStepPaddingWrapper>

                <CourseStepDivider />

                <CourseStepPaddingWrapper className="pb-2">
                  <EditableBox direction="column" className="mt-0">
                    <SpeakerHeading>{t("Conversation.Speaker.Vixi")}</SpeakerHeading>
                    <TransparentTextArea
                      minRows={4}
                      maxRows={4}
                      className="fs-20"
                      disabled={disabled}
                      {...register(`messages.${index}.learnerMessage`)}
                    />
                  </EditableBox>
                </CourseStepPaddingWrapper>
              </div>
            </ViewListItem>
          );
        })}
      </ViewList>

      <ConversationPagination {...viewList} visibleItems={5} onDeleteItem={removeSentence} />
      <Button
        disabled={disabled}
        className="mx-auto flex"
        size="pill"
        variant="outlined"
        onClick={addSentence}
      >
        {t("Conversation.AddSentence")}
      </Button>
    </>
  );
}

export default ConversationStep;
